import { StateCreator } from 'zustand';
import { CedarStore } from './types';
import type { JSONContent } from '@tiptap/core';
import { ChatInput } from './messagesSlice';

// Helper function to convert JSON content to legacy array format
export function convertJSONToLegacyFormat(
	json: JSONContent
): string | ChatInput[] {
	if (!json.content || !json.content.length) return '';

	const result: ChatInput[] = [];
	let currentText = '';

	// Process each node in the content
	for (const block of json.content) {
		if (block.content && Array.isArray(block.content)) {
			for (const node of block.content) {
				if (node.type === 'text' && typeof node.text === 'string') {
					currentText += node.text;
				} else if (node.type === 'choice' && node.attrs) {
					// If there's accumulated text, add it to the result
					if (currentText) {
						result.push(currentText);
						currentText = '';
					}

					// Add the choice node
					result.push({
						type: 'multiple_choice_suggestion',
						field: node.attrs.field || '',
						fieldType: 'userContext', // Default
						options: node.attrs.options || [],
						chosenValue: node.attrs.selectedOption || '',
					});
				}
			}
		}
	}

	// Add any remaining text
	if (currentText) {
		result.push(currentText);
	}

	// If there's only one string, return it directly
	if (result.length === 1 && typeof result[0] === 'string') {
		return result[0];
	}

	return result;
}

// Define structure for overrideInputContent
export interface OverrideInputContent {
	input: string | ChatInput[] | null;
}

// Define the chat input slice
export interface ChatInputSlice {
	// This is the up to date content of the chat input
	chatInputContent: JSONContent | null;
	// This is the content of the chat input that is used to override the chat input content from other slices
	overrideInputContent: OverrideInputContent;

	// Actions
	setChatInputContent: (content: JSONContent) => void;
	setOverrideInputContent: (content: string | ChatInput[] | null) => void;
}

// Create the chat input slice
export const createChatInputSlice: StateCreator<
	CedarStore,
	[],
	[],
	ChatInputSlice
> = (set) => {
	return {
		// Default state
		chatInputContent: null,
		overrideInputContent: { input: null },

		// Actions
		setChatInputContent: (content: JSONContent) => {
			set({ chatInputContent: content });
		},

		setOverrideInputContent: (content: string | ChatInput[] | null) => {
			// Always create a new object to ensure subscribers detect a change
			set({
				overrideInputContent: { input: content },
			});
		},
	};
};
